import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'

/** Lifecycle a provisioned WhatsApp number moves through. Mirrors the backend
 * `OrganizationPhoneNumber.Status` text choices. */
export const numberStatusEnum = {
  REQUESTED: 'requested',
  PROVISIONING: 'provisioning',
  PENDING_APPROVAL: 'pending_approval',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  RELEASED: 'released',
} as const

export type NumberStatusValue = (typeof numberStatusEnum)[keyof typeof numberStatusEnum]

export const numberStatusLabelMap: Record<NumberStatusValue, string> = {
  [numberStatusEnum.REQUESTED]: 'Requested',
  [numberStatusEnum.PROVISIONING]: 'Provisioning',
  [numberStatusEnum.PENDING_APPROVAL]: 'Pending approval',
  [numberStatusEnum.ACTIVE]: 'Active',
  [numberStatusEnum.SUSPENDED]: 'Suspended',
  [numberStatusEnum.RELEASED]: 'Released',
}

/** Statuses that are still settling — while any number is in one of these the
 * list is polled so the UI reflects async provisioning progress. */
export const PENDING_NUMBER_STATUSES: NumberStatusValue[] = [
  numberStatusEnum.REQUESTED,
  numberStatusEnum.PROVISIONING,
  numberStatusEnum.PENDING_APPROVAL,
]

export const organizationNumberShape = {
  id: z.string().uuid(),
  phoneNumber: z.string(),
  label: z.string(),
  status: z.enum(numberStatusEnum),
  isDefault: z.boolean(),
  supportsWhatsapp: z.boolean(),
  failureReason: z.string().nullable().optional(),
  subscriptionStatus: z.string().nullable().optional(),
  monthlyPrice: z.string().nullable().optional(),
  priceCurrency: z.string().nullable().optional(),
  canCancel: z.boolean(),
}

export const availableNumberShape = {
  phoneNumber: z.string(),
  friendlyName: z.string().nullable().optional(),
  country: z.string(),
  whatsappEligible: z.boolean(),
  monthlyPrice: z.string().nullable().optional(),
  priceUnit: z.string().nullable().optional(),
}

/** An org number a chat user may send from — the chat-facing read (no price). */
export const sendableNumberShape = {
  id: z.string().uuid(),
  phoneNumber: z.string(),
  label: z.string(),
  isDefault: z.boolean(),
}

/** Per-org "can this org send WhatsApp?" flag for the chat org selector. */
export const sendableNumberSummaryShape = {
  organization: z.string().uuid(),
  hasSendableNumber: z.boolean(),
}

export type OrganizationNumber = GetInferredFromRaw<typeof organizationNumberShape>
export type AvailableNumber = GetInferredFromRaw<typeof availableNumberShape>
export type SendableNumber = GetInferredFromRaw<typeof sendableNumberShape>
export type SendableNumberSummary = GetInferredFromRaw<typeof sendableNumberSummaryShape>
